import { useState } from 'react'
import { useCadastrarDia, useDiasNaoUteis } from '../../../api/queries/referenciasAdmin'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro, Esqueleto } from '../../../components/Estados'
import { Cartao } from '../../../components/ListaDeDados'
import { CampoDeSelecao, CampoDeTexto } from '../../../components/campos'
import { TEXTOS } from '../../../lib/glossario'
import { LinhaDoDiaNaoUtil } from './LinhaDoDiaNaoUtil'

const t = TEXTOS.referencias.calendario

/**
 * Os dias em que o relógio do prazo não anda.
 *
 * **O efeito é retroativo, e a tela diz isso antes.** Acrescentar ou remover um dia muda o cálculo
 * de horas úteis para todo chamado que atravesse aquela data — inclusive os já fechados, cujo prazo
 * é recalculado na próxima leitura. Não é defeito: é o que se quer quando um feriado municipal
 * estava faltando. Mas quem mexe precisa saber que o número de ontem pode ser outro hoje.
 *
 * **"Conferido" não é enfeite, e a tela agora diz quanto ele custa.** Ele separa "achamos que é
 * feriado" de "a ferramenta que carimba o prazo no chamado também considera feriado". Enquanto
 * houver um dia por conferir, o TIS não apura em competência nenhuma. A tela mostrava o estado ao
 * lado de cada dia, não dizia a consequência, e não oferecia o botão de conferir — os 32 dias do
 * calendário ficaram assim desde o começo.
 *
 * @returns o cartão do calendário
 */
export function CalendarioDoContrato() {
  const { data: dias, isPending, isError, error } = useDiasNaoUteis()
  const cadastrar = useCadastrarDia()
  const [dataNova, setDataNova] = useState('')
  const [rotulo, setRotulo] = useState('')
  const [abrangencia, setAbrangencia] = useState('MUNICIPAL')

  const porConferir = dias?.filter((dia) => !dia.conferido).length ?? 0

  async function salvar() {
    try {
      await cadastrar.mutateAsync({ data: dataNova, rotulo, abrangencia })
      setDataNova('')
      setRotulo('')
    } catch {
      // O AvisoDeErro abaixo diz o motivo.
    }
  }

  return (
    <Cartao titulo={t.titulo} descricao={t.ajuda}>
      {dias && dias.length > 0 && (
        <p
          className={
            porConferir > 0
              ? 'mb-3 max-w-prose rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900'
              : 'mb-3 max-w-prose rounded-lg bg-green-50 px-3 py-2 text-sm text-green-900'
          }
        >
          {porConferir > 0 ? t.pendencia(porConferir, dias.length) : t.tudoConferido(dias.length)}
          {porConferir > 0 && <span className="mt-1 block text-xs">{t.comoConferir}</span>}
        </p>
      )}

      <p className="mb-3 max-w-prose rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">{t.aviso}</p>

      {isPending && <Esqueleto linhas={4} />}
      {isError && <AvisoDeErro erro={error} titulo={TEXTOS.referencias.naoCarregou} />}

      {dias && (
        <ul className="divide-y divide-slate-100">
          {dias.map((dia) => (
            <LinhaDoDiaNaoUtil key={dia.data} dia={dia} />
          ))}
        </ul>
      )}

      <div className="mt-4 space-y-3 border-t border-slate-100 pt-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <CampoDeTexto rotulo="Data" type="date" value={dataNova} onChange={(e) => setDataNova(e.target.value)} />
          <CampoDeTexto
            rotulo="Nome"
            ajuda="Como o feriado é conhecido."
            value={rotulo}
            onChange={(e) => setRotulo(e.target.value)}
          />
          <CampoDeSelecao rotulo="Abrangência" value={abrangencia} onChange={(e) => setAbrangencia(e.target.value)}>
            <option value="MUNICIPAL">Feriado municipal</option>
            <option value="ESTADUAL">Feriado estadual</option>
            <option value="NACIONAL">Feriado nacional</option>
            {/* O contrato distingue feriado de ponto facultativo, e o calendário já traz os dois —
                o carnaval do seed é ponto facultativo. Faltar a opção aqui obrigaria a cadastrá-lo
                como feriado, mudando a natureza do registro para caber no formulário. */}
            <option value="PONTO_FACULTATIVO">Ponto facultativo</option>
          </CampoDeSelecao>
        </div>
        <Botao aparencia="primario" onClick={salvar} disabled={cadastrar.isPending || !dataNova || !rotulo.trim()}>
          {cadastrar.isPending ? 'Cadastrando…' : 'Cadastrar dia não útil'}
        </Botao>
        {cadastrar.isError && <AvisoDeErro erro={cadastrar.error} titulo={TEXTOS.referencias.naoCadastrou} />}
      </div>
    </Cartao>
  )
}
